import { CurrenciesSelect } from '@/components/CurrenciesSelect';
import { DeptSelect } from '@/components/DeptSelect';
import { ExItemSelect } from '@/components/ExItemSelect';
import { PAYEE_FORM_MAX_LENGTH } from '@/components/PayeeDetailForm/constants';
import { ProjectSelect } from '@/components/ProjectSelect';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useSubmitPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useSubmitPayeeInfo';
import {
  SECTION_KEYS,
  TransactionDetailForm,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  FormCheckbox,
  FormInput,
} from '@moneyforward/ap-frontend-components';
import { GetApPayeeInfoResponseApInvoiceTransactionDefaultValue } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import styles from '../../../styles.module.scss';
import { HeaderSection, SingleRow } from '../../Common';

const cx = classnames.bind(styles);

type EditProps = {
  apPayeeId: string;
  transactionDefaultValue?: GetApPayeeInfoResponseApInvoiceTransactionDefaultValue;
};

export const Edit: FC<EditProps> = memo(
  ({ apPayeeId, transactionDefaultValue }) => {
    const { isEditingSection, onEditFormDirtyChange, handleCancelEdit } =
      usePayeeDetailDrawer();
    const { t } = useTranslation();

    const defaultOptions = useMemo(() => {
      const deptOptions =
        transactionDefaultValue?.expense_dept?.id &&
        transactionDefaultValue?.expense_dept?.display_name
          ? [
              {
                value: transactionDefaultValue.expense_dept.id,
                label: transactionDefaultValue.expense_dept.display_name,
              },
            ]
          : [];

      const exItemOptions =
        transactionDefaultValue?.ap_item?.id &&
        transactionDefaultValue?.ap_item?.display_name
          ? [
              {
                value: transactionDefaultValue.ap_item.id,
                label: transactionDefaultValue.ap_item.display_name,
              },
            ]
          : [];

      const projectOptions =
        transactionDefaultValue?.project?.id &&
        transactionDefaultValue?.project?.display_name
          ? [
              {
                value: transactionDefaultValue.project.id,
                label: transactionDefaultValue.project.display_name,
              },
            ]
          : [];

      return { deptOptions, exItemOptions, projectOptions };
    }, [transactionDefaultValue]);

    const defaultValues: TransactionDetailForm = useMemo(
      () => ({
        name: transactionDefaultValue?.name ?? '',
        dept_id: transactionDefaultValue?.expense_dept?.id,
        ap_item_id: transactionDefaultValue?.ap_item?.id,
        project_id: transactionDefaultValue?.project?.id,
        is_tax_included: transactionDefaultValue?.is_tax_included ?? false,
        default_currency:
          transactionDefaultValue?.default_currency || undefined,
      }),
      [transactionDefaultValue]
    );

    const methods = useForm<TransactionDetailForm>({
      defaultValues,
      mode: 'all',
    });

    const { isValid, isDirty, isSubmitting } = useFormState({
      control: methods.control,
    });
    const { onSubmit, isLoading } = useSubmitPayeeInfo<TransactionDetailForm>({
      apPayeeId,
    });

    useEffect(() => {
      if (!isSubmitting) return;
      onEditFormDirtyChange?.(false);
    }, [isSubmitting, onEditFormDirtyChange]);

    useEffect(() => {
      onEditFormDirtyChange?.(isDirty);
    }, [isDirty, onEditFormDirtyChange]);

    const watchedDeptId = useWatch({
      control: methods.control,
      name: 'dept_id',
    });

    const handleCancel = useCallback(() => {
      handleCancelEdit?.(isDirty);
    }, [handleCancelEdit, isDirty]);

    const nameRules = {
      maxLength: {
        value: PAYEE_FORM_MAX_LENGTH.TRANSACTION_NAME,
        message: t(
          'ap_payee_detail_drawer_transaction_detail_item_name_max_length_error',
          { max: PAYEE_FORM_MAX_LENGTH.TRANSACTION_NAME }
        ),
      },
    };

    const selectProps = useMemo(
      () => ({
        placeholder: t('ap_payee_detail_drawer_select_placeholder'),
      }),
      [t]
    );

    const itemNameInputProps = useMemo(
      () => ({
        placeholder: t(
          'ap_payee_detail_drawer_transaction_detail_item_name_placeholder'
        ),
      }),
      [t]
    );

    return (
      <FormProvider {...methods}>
        <Form<TransactionDetailForm>
          formMethod={methods}
          onSubmit={onSubmit}
          testId='transaction-detail-edit'
          className={cx('sectionContainer')}
        >
          <HeaderSection
            displayName={t('ap_payee_detail_drawer_transaction_detail')}
            isSubHeader
            actionButton={
              <ButtonGroup direction='horizontal'>
                <ButtonV2
                  color='primary'
                  isSecondary
                  onClick={handleCancel}
                  isDisabled={isLoading}
                  testId='cancel-button'
                  containerClassName={cx('cancelButton')}
                >
                  {t('cancel')}
                </ButtonV2>
                <ButtonV2
                  color='primary'
                  size='xs'
                  type='submit'
                  isLoading={isLoading}
                  isTransparent={isLoading}
                  isDisabled={
                    !isValid ||
                    isLoading ||
                    isEditingSection !== SECTION_KEYS.TRANSACTION
                  }
                  testId='save-button'
                  buttonContainerClassName={cx('saveButton')}
                >
                  {t('save')}
                </ButtonV2>
              </ButtonGroup>
            }
          />
          <div className={cx('bodySectionContainer')}>
            <SingleRow
              label={t('ap_payee_detail_drawer_transaction_detail_item_label')}
              renderContent={
                <FormInput<TransactionDetailForm>
                  control={methods.control}
                  name='name'
                  inputId='transaction-item-name'
                  inputProps={itemNameInputProps}
                  rules={nameRules}
                />
              }
            />
            <SingleRow
              label={t(
                'ap_payee_detail_drawer_transaction_detail_expense_dept_label'
              )}
              renderContent={
                <DeptSelect<TransactionDetailForm>
                  control={methods.control}
                  name='dept_id'
                  inputId='expense-dept-id'
                  defaultOptions={defaultOptions.deptOptions}
                  selectProps={selectProps}
                  displayInactiveOptions
                />
              }
            />
            <SingleRow
              label={t(
                'ap_payee_detail_drawer_transaction_detail_expense_item_name_label'
              )}
              renderContent={
                <ExItemSelect<TransactionDetailForm>
                  control={methods.control}
                  name='ap_item_id'
                  inputId='ap-item-id'
                  defaultOptions={defaultOptions.exItemOptions}
                  deptId={watchedDeptId || undefined}
                  deptIdName='dept_id'
                  selectProps={selectProps}
                />
              }
            />
            <SingleRow
              label={t(
                'ap_payee_detail_drawer_transaction_detail_project_label'
              )}
              renderContent={
                <ProjectSelect<TransactionDetailForm>
                  control={methods.control}
                  name='project_id'
                  inputId='project-id'
                  defaultOptions={defaultOptions.projectOptions}
                  selectProps={selectProps}
                  displayInactiveOptions
                />
              }
            />
            <SingleRow
              label={t(
                'ap_payee_detail_drawer_transaction_detail_is_tax_included_label'
              )}
              renderContent={
                <FormCheckbox<TransactionDetailForm>
                  control={methods.control}
                  name='is_tax_included'
                  inputId='is-tax-included'
                  label={t('ap_payee_detail_drawer_transaction_detail_yes')}
                  className={cx('checkboxContainer')}
                />
              }
            />
            <SingleRow
              label={t(
                'ap_payee_detail_drawer_transaction_detail_currency_label'
              )}
              renderContent={
                <CurrenciesSelect<TransactionDetailForm>
                  control={methods.control}
                  name='default_currency'
                  inputId='default-currency'
                  selectProps={selectProps}
                />
              }
            />
          </div>
        </Form>
      </FormProvider>
    );
  }
);

Edit.displayName = 'TransactionDetailEdit';
